import React from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import { SortType } from "../../../../../../models/SortType";
import { OpsTatTableColumn } from "../../../../../../models/Dashboard/OpsTat/OpsTatDrilldown";
import { OpsTatStatus } from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import {
  formatDate,
} from "../../../../../../views/components/common/functions/formatDate";
import { getOpsTatStatusColors } from "./opsTatFormat";

const CELL_FONT_PX = 11;
const CHIP_FONT_PX = 10;
const CELL_PADDING = "4px 8px";
const ROW_STATUS_ALPHA = 0.12;
const ROW_STATUS_HOVER_ALPHA = 0.2;

const DATE_COLUMN_KEYS = new Set([
  "created",
  "dispatched",
  "entered_at",
  "exited_at",
  "ready_at",
  "project_created",
  "pr_raised",
  "created_date",
  "modified_date",
]);

interface OpsTatDrilldownTableProps {
  maxHeight: number;
  columns: OpsTatTableColumn[];
  rows: Array<Record<string, unknown>>;
  page_no: number;
  limit: number;
  total_count: number;
  sortableColumns: string[];
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
  rowsPerPageOptions?: number[];
}

const isDateOnlyValue = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

const isDateColumn = (column: OpsTatTableColumn): boolean => {
  try {
    if (column.type === "datetime") {
      return false;
    }
    return column.type === "date" || DATE_COLUMN_KEYS.has(column.key);
  } catch (error) {
    console.error(error);
    return column.type === "date";
  }
};

const formatUnknownText = (value: unknown): string => {
  try {
    if (value == null || value === "") {
      return "-";
    }
    return String(value);
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const formatTableDate = (value: unknown): string => {
  try {
    if (value == null || value === "") {
      return "-";
    }
    const raw = String(value).trim();
    const formatted = isDateOnlyValue(raw) ? raw : formatDate(value);
    return formatted || "-";
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const formatNumberCell = (value: unknown, unit?: string): string => {
  try {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return "-";
    }
    return `${value}${unit ?? ""}`;
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const getSerialNumber = (
  page: number,
  pageSize: number,
  index: number,
): number => {
  try {
    return (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + index + 1;
  } catch (error) {
    console.error(error);
    return index + 1;
  }
};

const rowKeyFor = (row: Record<string, unknown>, index: number): string => {
  try {
    const parts = [
      row.order_id,
      row.pr_id,
      row.product_id,
      row.vendor_id,
      row.ref,
      row.stream,
      row.is_open,
      index,
    ];
    return parts.map((part) => String(part ?? "")).join("-");
  } catch (error) {
    console.error(error);
    return `row-${index}`;
  }
};

const getColumnMinWidth = (column: OpsTatTableColumn): string => {
  try {
    if (column.type === "datetime") {
      return "160px";
    }
    if (isDateColumn(column)) {
      return "140px";
    }
    if (column.type === "number") {
      return "80px";
    }
    return "100px";
  } catch (error) {
    console.error(error);
    return "100px";
  }
};

const parseRowStatus = (value: unknown): OpsTatStatus => {
  try {
    if (value === "ok" || value === "good") {
      return "ok";
    }
    if (value === "warn" || value === "warning") {
      return "warn";
    }
    if (value === "bad") {
      return "bad";
    }
    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const getRowStatusTint = (
  theme: Theme,
  status: OpsTatStatus,
): { bg: string; hover: string } | null => {
  try {
    const customColors = theme.palette.customColors;
    if (status === "bad") {
      return {
        bg: alpha(customColors.errorRed, ROW_STATUS_ALPHA),
        hover: alpha(customColors.errorRed, ROW_STATUS_HOVER_ALPHA),
      };
    }
    if (status === "warn") {
      return {
        bg: alpha(theme.palette.goldenYellow.main, ROW_STATUS_ALPHA),
        hover: alpha(theme.palette.goldenYellow.main, ROW_STATUS_HOVER_ALPHA),
      };
    }
    if (status === "ok") {
      return {
        bg: alpha(customColors.successGreen, ROW_STATUS_ALPHA),
        hover: alpha(customColors.successGreen, ROW_STATUS_HOVER_ALPHA),
      };
    }
    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const OpsTatPill: React.FC<{
  columnKey: string;
  value: unknown;
}> = ({ columnKey, value }) => {
  const theme = useTheme<Theme>();

  try {
    if (columnKey === "status") {
      const status = parseRowStatus(value);
      const label = status ?? formatUnknownText(value);
      const tone = getOpsTatStatusColors(theme, status);
      return (
        <Box component="span" sx={{ color: tone.fg, fontWeight: 600 }}>
          {label}
        </Box>
      );
    }

    if (value == null || value === "") {
      return <>-</>;
    }

    let label = formatUnknownText(value);
    let tone = getOpsTatStatusColors(theme, null);

    if (columnKey === "path") {
      label = value === "custom" || value === "stock" ? String(value) : label;
    } else if (columnKey === "is_open") {
      const isOpen = value === true;
      label = isOpen ? "Open" : "Closed";
    } else if (
      columnKey === "is_swap" ||
      columnKey === "has_raw_promise" ||
      columnKey === "has_print_promise"
    ) {
      label = value === true ? "Yes" : "No";
    } else if (columnKey === "stream") {
      label = value === "raw" || value === "print" ? String(value) : label;
    }

    if (label === "-") {
      return <>-</>;
    }

    return (
      <Chip
        size="small"
        label={label}
        sx={{
          height: 18,
          fontSize: `${CHIP_FONT_PX}px`,
          fontWeight: 600,
          textTransform: columnKey === "path" ? "capitalize" : "none",
          bgcolor: tone.bg,
          color: tone.fg,
          border: `1px solid ${tone.fg}33`,
        }}
      />
    );
  } catch (error) {
    console.error(error);
    return <>-</>;
  }
};

const renderTableCell = (column: OpsTatTableColumn, value: unknown) => {
  try {
    if (column.type === "datetime") {
      return formatDate(value, true);
    }
    if (isDateColumn(column)) {
      return formatTableDate(value);
    }
    switch (column.type) {
      case "number":
        return formatNumberCell(value, column.unit);
      case "pill":
        return <OpsTatPill columnKey={column.key} value={value} />;
      case "text":
      default:
        return formatUnknownText(value);
    }
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const OpsTatDrilldownTable = ({
  maxHeight,
  columns,
  rows,
  page_no,
  limit,
  total_count,
  sortableColumns,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  rowsPerPageOptions,
}: OpsTatDrilldownTableProps) => {
  const theme = useTheme<Theme>();
  const headerColor = theme.palette.customColors.tableGrayHeadingColor;

  const handleRowsChange = (value: number) => {
    try {
      onRowsPerPageChange(value);
    } catch (error) {
      console.error(error);
    }
  };

  const renderSortIcon = (property: string) => {
    try {
      if (sortBy === property) {
        return sortType === SortType.ASC ? "↑" : "↓";
      }
      return "↑↓";
    } catch (error) {
      console.error(error);
      return "↑↓";
    }
  };

  const cellSx = {
    textAlign: "center" as const,
    fontSize: CELL_FONT_PX,
    padding: CELL_PADDING,
  };

  const bodyCellSx = {
    fontSize: `${CELL_FONT_PX}px`,
    textAlign: "center" as const,
    padding: CELL_PADDING,
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box>
        <TableContainer sx={{ maxHeight }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "relative", zIndex: 30 }}>
              <TableRow>
                <AppTableHeaderCell sx={{ ...cellSx, minWidth: "52px" }}>
                  #
                </AppTableHeaderCell>
                {columns.map((column) => {
                  const canSort = sortableColumns.includes(column.key);
                  return (
                    <AppTableHeaderCell
                      key={column.key}
                      sx={{
                        ...cellSx,
                        minWidth: getColumnMinWidth(column),
                      }}
                    >
                      {canSort ? (
                        <TableSortLabel
                          active={sortBy === column.key}
                          direction={sortType === SortType.ASC ? "asc" : "desc"}
                          onClick={() =>
                            onSortChange(
                              column.key,
                              sortBy === column.key &&
                                sortType === SortType.DESC
                                ? SortType.ASC
                                : SortType.DESC,
                            )
                          }
                          IconComponent={() => (
                            <span>{renderSortIcon(column.key)}</span>
                          )}
                          sx={{
                            fontSize: `${CELL_FONT_PX}px`,
                            color: headerColor,
                            "&.Mui-active": {
                              color: headerColor,
                            },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </AppTableHeaderCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                try {
                  const statusTint = getRowStatusTint(
                    theme,
                    parseRowStatus(row.status),
                  );
                  const rowBg =
                    statusTint?.bg ??
                    (index % 2 === 0
                      ? theme.palette.action.hover
                      : theme.palette.background.paper);
                  const hoverBg =
                    statusTint?.hover ?? theme.palette.action.hover;
                  return (
                    <TableRow
                      key={rowKeyFor(row, index)}
                      hover
                      sx={{
                        backgroundColor: rowBg,
                        "&.MuiTableRow-hover:hover": {
                          backgroundColor: hoverBg,
                        },
                      }}
                    >
                      <AppTableRowCell sx={bodyCellSx}>
                        {getSerialNumber(page_no, limit, index)}
                      </AppTableRowCell>
                      {columns.map((column) => {
                        const statusColor =
                          column.key === "status"
                            ? getOpsTatStatusColors(
                                theme,
                                parseRowStatus(row.status),
                              ).fg
                            : undefined;
                        return (
                          <AppTableRowCell
                            key={`${column.key}-${index}`}
                            color={statusColor}
                            sx={bodyCellSx}
                          >
                            {renderTableCell(column, row[column.key])}
                          </AppTableRowCell>
                        );
                      })}
                    </TableRow>
                  );
                } catch (error) {
                  console.error(error);
                  return null;
                }
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <AppTablePagenation
          fontSize={`${CELL_FONT_PX}px`}
          pageNo={page_no}
          limit={limit}
          totalCount={total_count}
          onPageChange={onPageChange}
          handleRowsChange={handleRowsChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </Box>
    </Paper>
  );
};

export default OpsTatDrilldownTable;
